import React, {useCallback, useState} from 'react';
import styles from './FileInputPanel.module.css';

// FileInputPanel 컴포넌트의 props 타입 정의
interface FileInputPanelProps {
    onFileSelect: (file: File) => void;  // 파일 선택 시 호출될 콜백 함수
    accept?: string;                     // 허용할 파일 확장자 (선택적)
    maxSize?: number;                    // 최대 파일 크기 (바이트 단위, 선택적)
    className?: string;
}

// 파일 입력 패널 컴포넌트
const FileInputPanel: React.FC<FileInputPanelProps> = ({
                                                           onFileSelect,
                                                           accept = ".jpg,.jpeg,.png,.gif,.bmp,.webp",  // 기본값으로 이미지 파일만 허용
                                                           maxSize = 10 * 1024 * 1024,                  // 기본 최대 크기 10MB
                                                           className
                                                       }) => {
    // 상태 관리
    const [isDragging, setIsDragging] = useState(false);         // 드래그 중인지 여부
    const [error, setError] = useState<string>("");              // 에러 메시지
    const [selectedFileName, setSelectedFileName] = useState<string>(""); // 선택된 파일명

    // 파일 유효성 검사 함수
    const validateFile = useCallback((file: File): boolean => {
        setError("");

        // 파일 크기 검증
        if (file.size > maxSize) {
            setError(`파일 크기는 ${maxSize / (1024 * 1024)}MB 이하여야 합니다.`);
            return false;
        }

        return true;
    }, [accept, maxSize]);

    // MIME 타입에서 확장자만 추출
    const readableExtensions = accept
        .split(',')
        .map(type => {
            const parts = type.trim().split('/');
            return parts.length === 2 ? parts[1] : parts[0].replace('.', '');
        })
        .join(', ');

    // 파일 처리 함수
    const handleFile = useCallback((file: File) => {
        if (validateFile(file)) {
            setSelectedFileName(file.name);  // 파일명 상태 업데이트
            onFileSelect(file);              // 콜백 함수 호출
        }
    }, [validateFile, onFileSelect]);

    // 드래그 이벤트 핸들러들
    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);  // 드래그 상태 활성화
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);  // 드래그 상태 비활성화
    }, []);

    // 파일 드롭 핸들러
    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const file = e.dataTransfer.files[0];  // 첫 번째 파일만 처리
        if (file) {
            handleFile(file);
        }
    }, [handleFile]);

    // 파일 입력 변경 핸들러
    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];  // 첫 번째 파일만 처리
        if (file) {
            handleFile(file);
        }
    }, [handleFile]);

    return (
        <div className={`${styles.container} ${className || ''}`}>
            {/* 헤더 영역 */}
            <div className={styles.header}>
                <div className={styles.title}>Upload File</div>
                <label className={styles.extension}>{readableExtensions}</label>
            </div>

            {/* 드롭 존 영역 */}
            <div
                className={`${styles.dropZone} 
                    ${isDragging ? styles.dragging : ''} 
                    ${selectedFileName ? styles.hasFile : ''}`}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {/* 파일 미선택 시 표시될 내용 */}
                {!selectedFileName ? (
                    <>
                        {/* 파일 아이콘 */}
                        <div className={styles.iconContainer}>
                            <svg
                                className={styles.icon}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                                <line x1="12" y1="18" x2="12" y2="12"/>
                                <line x1="9" y1="15" x2="15" y2="15"/>
                            </svg>
                        </div>

                        {/* 파일 업로드 안내 메시지 */}
                        <div className={styles.instructions}>
                            <p>Drag n Drop here</p>
                            <p className={styles.or}>Or</p>
                            <label className={styles.browseButton}>
                                Browse
                                <input
                                    type="file"
                                    className={styles.hiddenInput}
                                    onChange={handleInputChange}
                                    accept={accept}
                                />
                            </label>
                        </div>
                    </>
                ) : (
                    // 파일 선택 시 표시될 내용
                    <div className={styles.fileInfo}>
                        {/* 파일 아이콘 */}
                        <div className={styles.fileIcon}>
                            <svg
                                className={styles.icon}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                                <polyline points="13 2 13 9 20 9"></polyline>
                            </svg>
                        </div>
                        {/* 파일 정보 표시 */}
                        <div className={styles.fileDetails}>
                            <p className={styles.fileName}>{selectedFileName}</p>
                        </div>
                        {/* 파일 제거 버튼 */}
                        <button
                            className={styles.removeButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFileName("");  // 파일명 초기화
                                setError("");            // 에러 메시지 초기화
                            }}
                            type="button"
                        >
                            ✕
                        </button>
                    </div>
                )}
            </div>

            {/* 에러 메시지 표시 영역 */}
            {error && <div className={styles.error}>{error}</div>}
        </div>
    );
};

export default FileInputPanel;